/**
 * Talking to whoever lives here (M4-08, the smith's half added in M5-01).
 *
 * A focused conversation model for the popup in the client shell. Topics still
 * come from content rather than a hard-coded dialogue tree, but the player sees
 * them as response buttons and the villager's reply returns to the same window.
 *
 * Two things make this menu different from the aim menu or the gestures. First,
 * **what it offers is content**: the topics come from the villager's own
 * `topics` list, so the Meddyg's menu has no "buy" on it and the herders' has
 * nothing but news, and neither of those is a rule written here. Second, buying
 * needs a *second* choice — which thing off the stall — so `choose` can answer
 * "here is a new prompt, stay open" as well as "send this". That is one stage,
 * not a tree: the stall is the only place in the game where naming a topic is
 * not enough on its own.
 *
 * Pure state. It knows nothing about the DOM and nothing about the socket.
 */

import { errand, villager } from '@annwn/content';
import type { EntitySnapshot, SelfSnapshot } from '@annwn/protocol';
import type { MenuItem } from '@annwn/ui';

/** Every topic the sim's `TOPICS` vocabulary has, as the client names them. */
export type TalkTopic =
  'greet' | 'treat' | 'bed' | 'rumour' | 'trade' | 'buy' | 'repair' | 'errand';

/**
 * The key each topic answers to, in the order they are offered.
 *
 * Fixed keys rather than positional ones, deliberately: `r` is news whether you
 * are talking to the Elder or the smith, so a player learns one keyboard rather
 * than one per person. A villager who does not offer a topic simply has no entry
 * for it in their prompt.
 */
export const TALK_KEYS: readonly { readonly key: string; readonly topic: TalkTopic }[] = [
  { key: 'g', topic: 'greet' },
  { key: 't', topic: 'treat' },
  { key: 'b', topic: 'bed' },
  { key: 'r', topic: 'rumour' },
  { key: 'w', topic: 'trade' },
  { key: 'p', topic: 'buy' },
  { key: 'm', topic: 'repair' },
];

const TOPIC_OF_KEY = new Map(TALK_KEYS.map((entry) => [entry.key, entry.topic]));

/** The word the prompt shows. What you are asking for, in a player's words. */
const LABEL: { readonly [T in TalkTopic]: string } = {
  greet: 'greet',
  treat: 'ask for help',
  bed: 'ask about the bed',
  rumour: 'ask for news',
  trade: 'ask what they have',
  buy: 'buy',
  repair: 'ask for an edge',
  errand: 'speak of the work',
};

export interface TalkMenuRequest {
  readonly title: string;
  readonly subtitle: string;
  readonly items: readonly MenuItem[];
}

/** Keys the stall menu offers its wares under, in order. */
const WARE_KEYS = '123456789abcdefghij';

/** The def this entity is, or `null` for anybody who is not a villager. */
function defOf(target: EntitySnapshot) {
  if (target.defId === undefined) return null;
  try {
    return villager(target.defId);
  } catch {
    // A world running content this client does not have. They get a greeting
    // and nothing else, which is a worse conversation and not a broken one.
    return null;
  }
}

/**
 * The prompt for somebody, offering only what they actually answer.
 *
 * Falls back to a greeting when the def cannot be resolved, rather than to the
 * full list: offering a player a key that will be refused out loud by an NPC is
 * how a UI teaches that its own menu is a guess.
 */
export function talkPrompt(target: EntitySnapshot): string {
  const def = defOf(target);
  const topics: readonly string[] = def ? def.topics : ['greet'];

  const offered = TALK_KEYS.filter(({ topic }) => topics.includes(topic))
    .map(({ key, topic }) => `${LABEL[topic]} (${key})`)
    .join(' · ');

  return `${target.name}: ${offered}`;
}

/** What is on their stall, as a prompt. Empty when they keep nothing. */
export function stallPrompt(target: EntitySnapshot): string | null {
  const wares = defOf(target)?.stall?.wares ?? [];
  if (wares.length === 0) return null;

  const offered = wares
    .slice(0, WARE_KEYS.length)
    .map((ware, index) => `${ware.id} (${WARE_KEYS[index]}) — ${ware.price}`)
    .join(' · ');

  return `Buy what? ${offered}`;
}

/** Is this somebody you can talk to at all? Villagers, and nobody else yet. */
export function isTalkable(entity: EntitySnapshot): boolean {
  return entity.kind === 'villager' && !(entity.combat?.down ?? false);
}

export interface TalkChoice {
  readonly targetId: string;
  readonly topic: TalkTopic;

  /** Which ware, for `buy`. Absent for every other topic. */
  readonly subject?: string;
}

/**
 * What choosing a response did: something to send, or a new prompt to show while
 * the menu stays open. `null` from `choose` means neither — the menu closes.
 */
export type TalkResult =
  | { readonly kind: 'send'; readonly choice: TalkChoice }
  | { readonly kind: 'prompt'; readonly text: string };

export class TalkMenu {
  private target: EntitySnapshot | null = null;

  /** The viewer's own active work, refreshed from every authoritative snapshot. */
  private self: SelfSnapshot | null = null;

  /** Kept across snapshot-driven redraws while a reply is on screen. */
  private subtitle = 'What do you say?';
  private waiting = false;

  /** `true` once `buy` has been chosen and the stall is being read. */
  private buying = false;

  get active(): boolean {
    return this.target !== null;
  }

  get targetId(): string | null {
    return this.target?.id ?? null;
  }

  /** Refresh contextual responses without closing the conversation. */
  updateSelf(self: SelfSnapshot | null): void {
    this.self = self;
  }

  /** Open against somebody who lives here. Refused for anybody else. */
  show(target: EntitySnapshot): string | null {
    if (!isTalkable(target)) return null;
    this.target = target;
    this.buying = false;
    this.subtitle = 'What do you say?';
    this.waiting = false;
    return talkPrompt(target);
  }

  close(): void {
    this.target = null;
    this.buying = false;
    this.subtitle = 'What do you say?';
    this.waiting = false;
  }

  /** Active errands whose own data contributes a response to this speaker. */
  private errandResponses(target: EntitySnapshot): readonly {
    readonly id: string;
    readonly key: string;
    readonly label: string;
  }[] {
    if (!target.defId) return [];
    const responses: { id: string; key: string; label: string }[] = [];
    for (const active of this.self?.sheet.errands ?? []) {
      try {
        const def = errand(active.id);
        if (def.giver !== target.defId || !def.dialogue) continue;
        responses.push({
          id: def.id,
          key: String(responses.length + 1),
          label: def.dialogue.active,
        });
      } catch {
        // A newer server can hand an older client an errand it does not know.
        // Its Journal remains readable; it simply contributes no guessed line.
      }
    }
    return responses;
  }

  /** The focused popup model for the current stage of the conversation. */
  request(reply?: string, waiting = false): TalkMenuRequest | null {
    const target = this.target;
    if (!target) return null;

    if (reply !== undefined) {
      this.subtitle = reply;
      this.waiting = waiting;
    }

    if (this.buying) {
      const wares = defOf(target)?.stall?.wares ?? [];
      return {
        title: target.name,
        subtitle: 'What would you like to buy?',
        items: wares.slice(0, WARE_KEYS.length).map((ware, index) => ({
          id: `ware:${ware.id}`,
          key: WARE_KEYS[index],
          name: ware.id,
          meta: String(ware.price),
        })),
      };
    }

    const topics: readonly string[] = defOf(target)?.topics ?? ['greet'];
    const contextual = this.errandResponses(target);
    return {
      title: target.name,
      subtitle: this.subtitle,
      items: this.waiting
        ? []
        : [
            ...contextual.map((response) => ({
              id: `errand:${response.id}`,
              key: response.key,
              name: response.label,
            })),
            ...TALK_KEYS.filter(({ topic }) => topics.includes(topic)).map(({ key, topic }) => ({
              id: `topic:${topic}`,
              key,
              name: LABEL[topic],
            })),
          ],
    };
  }

  /** Resolve a clicked popup row through the same path as its keyboard shortcut. */
  chooseId(id: string): TalkResult | null {
    const target = this.target;
    if (!target) return null;
    let result: TalkResult | null = null;
    if (id.startsWith('errand:')) {
      const defId = id.slice('errand:'.length);
      const offered = this.errandResponses(target).some((one) => one.id === defId);
      result = offered
        ? {
            kind: 'send',
            choice: { targetId: target.id, topic: 'errand', subject: defId },
          }
        : null;
    } else if (id.startsWith('topic:')) {
      const topic = id.slice('topic:'.length) as TalkTopic;
      const entry = TALK_KEYS.find((one) => one.topic === topic);
      result = entry ? this.choose(entry.key) : null;
    } else if (id.startsWith('ware:') && this.target && this.buying) {
      const wareId = id.slice('ware:'.length);
      const index = (defOf(this.target)?.stall?.wares ?? []).findIndex(
        (ware) => ware.id === wareId,
      );
      result = index >= 0 && index < WARE_KEYS.length ? this.choose(WARE_KEYS[index]!) : null;
    }

    // The legacy key prompt was one-shot; the popup is a conversation. Keep its
    // speaker after sending so the reply can land in the same window and offer
    // another response without making the player reopen it.
    if (result?.kind === 'send' && target) {
      this.target = target;
      this.buying = false;
    }
    return result;
  }

  choose(key: string): TalkResult | null {
    const target = this.target;
    if (!target) return null;

    const pressed = key.toLowerCase();

    // Second stage: naming one thing off the stall.
    if (this.buying) {
      const wares = defOf(target)?.stall?.wares ?? [];
      const index = WARE_KEYS.indexOf(pressed);
      const ware = index >= 0 ? wares[index] : undefined;
      this.close();
      if (!ware) return null;
      return { kind: 'send', choice: { targetId: target.id, topic: 'buy', subject: ware.id } };
    }

    const contextual = this.errandResponses(target).find((response) => response.key === pressed);
    if (contextual) {
      this.close();
      return {
        kind: 'send',
        choice: { targetId: target.id, topic: 'errand', subject: contextual.id },
      };
    }

    const topic = TOPIC_OF_KEY.get(pressed);
    // Only what they actually answer. A key for a topic this person does not
    // keep closes the menu rather than sending a request that will be refused
    // out loud — the prompt never offered it.
    if (!topic || !(defOf(target)?.topics.includes(topic) ?? topic === 'greet')) {
      this.close();
      return null;
    }

    if (topic === 'buy') {
      const prompt = stallPrompt(target);
      if (!prompt) {
        this.close();
        return null;
      }
      this.buying = true;
      return { kind: 'prompt', text: prompt };
    }

    this.close();
    return { kind: 'send', choice: { targetId: target.id, topic } };
  }
}
